<template>
  <div class="activity-table">
    <!-- Skeleton Loader -->
    <div v-if="loading" class="skeleton-container">
      <!-- Header Skeleton -->
      <div class="activity-table-header d-flex align-items-center w-100 flex-wrap" style="gap: 8px">
        <div class="flex-grow-1">
          <div class="row g-2 align-items-center">
            <div class="col-auto">
              <div class="skeleton-input skeleton-search"></div>
            </div>
            <div class="col-auto">
              <div class="skeleton-input skeleton-select"></div>
            </div>
            <div class="col-auto">
              <div class="skeleton-input skeleton-date"></div>
            </div>
          </div>
        </div>
        <div class="d-flex align-items-center ms-auto pe-0">
          <div class="skeleton-button skeleton-export"></div>
        </div>
      </div>

      <!-- Table Skeleton -->
      <div class="skeleton-table">
        <!-- Table Header -->
        <div class="skeleton-table-header">
          <div class="skeleton-cell skeleton-cell-header" v-for="i in 5" :key="i"></div>
        </div>
        <!-- Table Rows -->
        <div class="skeleton-table-row" v-for="i in 5" :key="i">
          <div class="skeleton-cell" v-for="j in 5" :key="j"></div>
        </div>
      </div>
    </div>

    <!-- Actual Content -->
    <div v-else>
      <div class="activity-table-header d-flex align-items-center w-100 flex-wrap" style="gap: 8px">
        <div class="flex-grow-1">
          <div class="row g-2 align-items-center">
            <div class="col-auto">
              <div class="input-wrapper search-input-wrapper">
                <span class="input-icon icon-left">
                  <i class="fa fa-search"></i>
                </span>
                <input type="text" placeholder="Search Ref ID" v-model="search" class="search-input styled-input" />
              </div>
            </div>
            <div class="col-auto">
              <div class="input-wrapper status-input-wrapper">
                <select v-model="status" class="status-select styled-input">
                  <option value="" selected>All</option>
                  <option v-for="option in statusOptions" :key="option.value" :value="option.value">
                    {{ option.text }}
                  </option>
                </select>
                <span class="input-icon icon-right select-arrow">
                  <i class="fa fa-chevron-down"></i>
                </span>
              </div>
            </div>
            <div class="col-auto">
              <SwiftDatePicker v-model="dateRange" placeholder="Select Date Range" />
            </div>
          </div>
        </div>
        <div class="d-flex align-items-center ms-auto pe-0 export-btn-responsive" style="min-width: 0">
          <argon-button variant="export" @click="exportToCSV">
            Export
          </argon-button>
        </div>
      </div>

      <SwiftDataTable :headers="columns" :items="filteredActivities" :search-value="search"
        :rows-per-page="pagination.per_page" hide-footer>
        <template #item-amount="{ amount, type }">
          <span :class="amountClass(type)">{{ formatAmount(amount, type) }}</span>
        </template>

        <template #item-transaction_id="{ transaction }">
          {{ transaction && transaction.transaction_id ? transaction.transaction_id : '-' }}
        </template>

        <template #item-reference_id="{ transaction }">
          {{ transaction && transaction.reference_id ? transaction.reference_id : '-' }}
        </template>

        <template #item-latest_balance="{ latest_balance }">
          IDR {{ formatNumber(latest_balance) }}
        </template>
      </SwiftDataTable>

      <!-- Pagination Controls -->
      <div class="pagination-controls d-flex align-items-center justify-content-between mt-4"
        v-if="!loading && activities.length > 0">
        <div class="per-page-control d-flex align-items-center">
          <span class="me-2">Show:</span>
          <div class="select-with-icon position-relative" style="position: relative">
            <select class="form-select form-select-sm" v-model="pagination.per_page" @change="onPerPageChange">
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="500">500</option>
            </select>
          </div>
        </div>

        <div class="d-flex align-items-center">
          <span class="pagination-info me-3">
            Showing {{ displayStart }} to {{ displayEnd }} of {{ totalFiltered }} entries
          </span>

          <!-- <nav aria-label="Balance history pagination">
            <ul class="pagination mb-0">
              <li class="page-item" :class="{ disabled: !hasPrevPage }">
                <button class="page-link" @click="onPreviousPageFiltered" :disabled="!hasPrevPage">
                  <i class="fas fa-chevron-left"></i>
                </button>
              </li>
              <li class="page-item">
                <span class="page-link bg-primary text-white">{{ pagination.current_page }}</span>
              </li>
              <li class="page-item" :class="{ disabled: !hasNextPage }">
                <button class="page-link" @click="onNextPageFiltered" :disabled="!hasNextPage">
                  <i class="fas fa-chevron-right"></i>
                </button>
              </li>
            </ul>
          </nav> -->
          <nav aria-label="Balance history pagination">
            <ul class="pagination mb-0">
              <!-- Prev -->
              <li class="page-item" :class="{ disabled: !hasPrevPage }">
                <button
                  class="page-link"
                  @click="goToPage(pagination.current_page - 1)"
                  :disabled="!hasPrevPage"
                >
                  <i class="fas fa-chevron-left"></i>
                </button>
              </li>

              <!-- Page Numbers -->
              <li
                v-for="page in visiblePages"
                :key="page"
                class="page-item"
                :class="{ active: page === pagination.current_page }"
              >
                <button class="page-link" @click="goToPage(page)">
                  {{ page }}
                </button>
              </li>

              <!-- Next -->
              <li class="page-item" :class="{ disabled: !hasNextPage }">
                <button
                  class="page-link"
                  @click="goToPage(pagination.current_page + 1)"
                  :disabled="!hasNextPage"
                >
                  <i class="fas fa-chevron-right"></i>
                </button>
              </li>

            </ul>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ArgonButton from "@/components/ArgonButton.vue";
import SwiftDataTable from "@/components/SwiftDataTable.vue";
import SwiftDatePicker from "@/components/SwiftDatePicker.vue";
import swal from "sweetalert2";
export default {
  name: "BalanceActivityTable",
  props: {
    activities: {
      type: Array,
      required: true,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    paginationData: {
      type: Object,
      default: () => ({
        current_page: 1,
        per_page: 20,
        total: 0,
        last_page: 1,
        next_page_url: null,
        prev_page_url: null
      })
    },
    initialFilters: {
      type: Object,
      default: () => ({})
    },
  },
  components: {
    ArgonButton,
    SwiftDataTable,
    SwiftDatePicker,
  },
  data() {
    // Set default date range to today (one day)
    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    return {
      // dateRange with default one-day range (today)
      dateRange: [todayStart, todayEnd],
      search: "",
      status: "",
      initialFiltersApplied: false,
      searchDebounceTimer: null,
      pagination: {
        current_page: 1,
        per_page: 20,
        total: 0,
        last_page: 1,
        next_page_url: null,
        prev_page_url: null
      },
      statusOptions: [
        { text: "Transfer", value: "transfer" },
        { text: "Transfer Fee", value: "transfer fee" },
        { text: "Refund", value: "refund" },
        { text: "Top Up", value: "topup" },
      ],
      searchField: "",
      columns: [
        { text: "Ref ID", value: "transaction_uuid", sortable: true, },
        { text: "Transaction ID", value: "transaction_id", sortable: true },
        { text: "Reference ID", value: "reference_id", sortable: true },
        { text: "Date & Time", value: "recorded_at", sortable: true },
        { text: "Type", value: "source", sortable: true },
        { text: "Total Amount", value: "amount", sortable: true },
        { text: "Ending Balance", value: "latest_balance", sortable: true },
      ],
    };
  },
  computed: {
    // Get filtered data for current page display (always use server-side data)
    filteredActivities() {
      return this.activities;
    },

    // Calculate display values for pagination info (always use server pagination)
    totalFiltered() {
      return this.pagination.total;
    },

    displayStart() {
      if (this.totalFiltered === 0) return 0;
      return Math.min((this.pagination.current_page - 1) * this.pagination.per_page + 1, this.totalFiltered);
    },

    displayEnd() {
      if (this.totalFiltered === 0) return 0;
      return Math.min(this.pagination.current_page * this.pagination.per_page, this.totalFiltered);
    },

    // Calculate if there are previous/next pages (always use server pagination)
    hasPrevPage() {
      return this.pagination.current_page > 1;
    },

    hasNextPage() {
      return !!this.pagination.next_page_url;
    },

    totalPages() {
      return this.pagination.last_page || Math.ceil(this.totalFiltered / this.pagination.per_page);
    },
    visiblePages() {
      const current = this.pagination.current_page;
      const last = this.pagination.last_page;
      const range = 2; // kiri & kanan (total 5 page)

      let start = Math.max(1, current - range);
      let end = Math.min(last, current + range);

      const pages = [];
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    },
  },
  watch: {
    paginationData: {
      handler(newVal) {
        if (newVal) {
          this.pagination = { ...newVal };
        }
      },
      immediate: true,
      deep: true
    },

    initialFilters: {
      handler(newFilters) {
        // Only process once to prevent double API calls
        if (this.initialFiltersApplied) {
          return;
        }

        // If there are actual filters to apply (from URL parameters)
        if (newFilters && Object.keys(newFilters).length > 0) {
          // Apply initial filters from URL parameters
          if (newFilters.search) {
            this.search = newFilters.search;
          }
          if (newFilters.status) {
            this.status = newFilters.status;
          }
          if (newFilters.dateRange) {
            this.dateRange = newFilters.dateRange;
          }
        }

        // Mark as applied and trigger server call with current filters
        // (either from URL params or default date filter)
        this.initialFiltersApplied = true;
        this.$nextTick(() => {
          this.triggerServerFilter();
        });
      },
      immediate: true,
      deep: true
    },

    // Reset to page 1 and trigger server-side filtering when filters change
    search: {
      handler(newVal, oldVal) {
        // Only trigger if this is a user change, not initial setting
        if (this.initialFiltersApplied && oldVal !== newVal) {
          // Clear existing debounce timer
          if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer);
          }

          // Set new debounce timer (500ms delay)
          this.searchDebounceTimer = setTimeout(() => {
            this.pagination.current_page = 1;
            this.triggerServerFilter();
          }, 500);
        }
      }
    },

    status: {
      handler(newVal, oldVal) {
        // Only trigger if this is a user change, not initial setting
        if (this.initialFiltersApplied && oldVal !== newVal) {
          this.pagination.current_page = 1;
          this.triggerServerFilter();
        }
      }
    },

    dateRange: {
      handler(newVal, oldVal) {
        // Only trigger if this is a user change, not initial setting
        if (this.initialFiltersApplied && oldVal !== newVal) {
          this.pagination.current_page = 1;
          this.triggerServerFilter();
        }
      },
      deep: true
    }
  },

  methods: {
    goToPage(page) {
        if (
          page < 1 ||
          page > this.pagination.last_page ||
          page === this.pagination.current_page
        ) return;

        this.$emit(
          "page-change",
          page,
          this.pagination.per_page,
          this.getCurrentFilters()
        );
      },
    // Trigger server-side filtering
    triggerServerFilter() {
      const filters = this.getCurrentFilters();
      this.$emit('page-change', this.pagination.current_page, this.pagination.per_page, filters);
    },

    formatAmount(amount, type) {
      if (type === "debit") {
        return `-IDR ${Math.abs(amount).toLocaleString("id-ID")}`;
      } else {
        return `+IDR ${Math.abs(amount).toLocaleString("id-ID")}`;
      }
    },
    formatCurrency(val) {
      return `IDR ${val.toLocaleString("id-ID")}`;
    },
    amountClass(type) {
      if (type === "debit") {
        return "amount-negative";
      } else {
        return "amount-positive";
      }
    },
    formatNumber(amount) {
      if (amount === null || amount === undefined) return "0";
      const num = Number(amount);
      if (isNaN(num)) return amount; // Return original if not a number (e.g. for error messages)
      return num.toLocaleString("id-ID"); // Using Indonesian locale for formatting like 120.000.000
    },
    stripTime(date) {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    },
    // Pagination methods
    onPreviousPage() {
      if (this.pagination.current_page > 1) {
        this.$emit('page-change', this.pagination.current_page - 1, this.pagination.per_page);
      }
    },
    onNextPage() {
      if (this.pagination.current_page < this.pagination.last_page) {
        this.$emit('page-change', this.pagination.current_page + 1, this.pagination.per_page);
      }
    },
    onPerPageChange() {
      // Always emit to parent to handle server-side pagination
      this.$emit('page-change', 1, this.pagination.per_page, this.getCurrentFilters());
    },

    // Handle pagination for filtered data
    onPreviousPageFiltered() {
      if (this.hasPrevPage) {
        // Always use server-side pagination
        this.$emit('page-change', this.pagination.current_page - 1, this.pagination.per_page, this.getCurrentFilters());
      }
    },

    onNextPageFiltered() {
      if (this.hasNextPage) {
        // Always use server-side pagination
        this.$emit('page-change', this.pagination.current_page + 1, this.pagination.per_page, this.getCurrentFilters());
      }
    },

    // Get current filter values
    getCurrentFilters() {
      return {
        search: this.search,
        status: this.status,
        dateRange: this.dateRange
      };
    },
    exportToCSV() {
      // Check if there's any data to export
      if (!this.activities || this.activities.length === 0) {
        swal.fire("No data to export");
        return;
      }

      // Emit event to parent component to handle export with current filters
      const filters = this.getCurrentFilters();
      this.$emit('export-csv', filters);
    },
    // Helper method for parent component to format and download CSV
    downloadCSV(dataToExport, filename = null) {
      if (!dataToExport || dataToExport.length === 0) {
        swal.fire("No data to export");
        return;
      }

      // Define CSV headers
      const headers = [
        "Ref ID",
        "Transaction ID",
        "Reference ID",
        "Date & Time",
        "Type",
        "Total Amount",
        "Ending Balance",
      ];

      // Convert data to CSV format
      const csvContent = [
        headers.join(","), // Header row
        ...dataToExport.map((item) =>
          [
            `"${item.transaction_uuid || ""}"`,
            `"${item.transaction && item.transaction.transaction_id ? item.transaction.transaction_id : "-"}"`,
            `"${item.transaction && item.transaction.reference_id ? item.transaction.reference_id : "-"}"`,
            `"${item.recorded_at || ""}"`,
            `"${item.source || ""}"`,
            `"${this.formatAmount(item.amount, item.type)}"`,
            `"IDR ${this.formatNumber(item.latest_balance)}"`,
          ].join(",")
        ),
      ].join("\n");

      // Create and download file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);

        // Generate filename with current date and filters if not provided
        if (!filename) {
          const now = new Date();
          const dateStr = now.toISOString().split("T")[0]; // YYYY-MM-DD format
          const timeStr = now.toTimeString().split(" ")[0].replace(/:/g, ""); // HHMMSS format

          // Build filename with filter info
          let filterInfo = "";
          if (this.search) filterInfo += `_search-${this.search.substring(0, 10)}`;
          if (this.status) filterInfo += `_status-${this.status.replace(/\s+/g, "-").toLowerCase()}`;
          if (this.dateRange && this.dateRange.length === 2) {
            const startDate = this.dateRange[0] instanceof Date ? this.dateRange[0] : new Date(this.dateRange[0]);
            const endDate = this.dateRange[1] instanceof Date ? this.dateRange[1] : new Date(this.dateRange[1]);
            filterInfo += `_from-${startDate.toISOString().split("T")[0]}_to-${endDate.toISOString().split("T")[0]}`;
          }

          filename = `balance-activity${filterInfo}_${dateStr}_${timeStr}.csv`;
        }
        link.setAttribute("download", filename);

        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
  },
  beforeUnmount() {
    // Clean up debounce timer
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
  }
};
</script>

<style scoped>
@media (max-width: 768px) {
  .activity-table-header {
    flex-direction: column !important;
    align-items: stretch !important;
  }

  .export-btn-responsive {
    width: 100% !important;
    margin-top: 12px;
    justify-content: flex-end !important;
  }

  .export-btn-responsive>.btn {
    width: 100%;
  }
}

/* Keep styles for the overall layout and other elements */
.activity-table {
  width: 100%;
}

.activity-table-header {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  align-items: center;
}

/* Shared wrapper style (if not fully encapsulated in BaseDateRangePicker) */
.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

/* Base styling for inputs/select (used by search and status) */
.styled-input {
  height: 40px;
  border-radius: 8px !important;
  border: 1px solid #e2e8f0 !important;
  background-color: #f8fafc !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  font-size: 14px !important;
  color: #4a5568;
  box-sizing: border-box;
  width: 100%;
  transition: border-color 0.2s ease-in-out;
}

.styled-input:focus {
  border-color: #4299e1 !important;
  outline: none;
}

.styled-input::placeholder {
  color: #a0aec0;
  opacity: 1;
}

/* Search Input */
.search-input-wrapper {
  flex-basis: 250px;
}

.search-input.styled-input {
  padding-left: 40px !important;
}

/* Status Select */
.status-input-wrapper {
  min-width: 150px;
}

.status-select.styled-input {
  padding-left: 12px !important;
  padding-right: 35px !important;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  cursor: pointer;
}

.status-select option[disabled] {
  color: #a0aec0;
}

/* Icon positioning (shared) */
.input-icon {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.icon-left {
  left: 12px;
}

.icon-right {
  /* Still used by select */
  right: 12px;
}

.select-arrow {
  /* Still used by select */
  pointer-events: none;
}

/* Export Button */
.export-btn {
  margin-left: auto;
  height: 40px;
  display: inline-flex;
  align-items: center;
}

/* Amount styles */
.amount-positive {
  color: #1fb97f;
}

.amount-negative {
  color: #f44336;
}

/* Skeleton Loader Styles */
.skeleton-container {
  animation: pulse 1.5s ease-in-out infinite;
}

.skeleton-input,
.skeleton-button,
.skeleton-cell {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
}

.skeleton-search {
  width: 250px;
  height: 40px;
}

.skeleton-select {
  width: 150px;
  height: 40px;
}

.skeleton-date {
  width: 200px;
  height: 40px;
}

.skeleton-export {
  width: 80px;
  height: 40px;
}

.skeleton-table {
  margin-top: 24px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.skeleton-table-header {
  display: flex;
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  padding: 12px;
  gap: 12px;
}

.skeleton-table-row {
  display: flex;
  border-bottom: 1px solid #e2e8f0;
  padding: 12px;
  gap: 12px;
}

.skeleton-table-row:last-child {
  border-bottom: none;
}

.skeleton-cell {
  flex: 1;
  height: 20px;
}

.skeleton-cell-header {
  height: 24px;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }

  100% {
    background-position: 200% 0;
  }
}

@keyframes pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.8;
  }
}
</style>
